import { apiRequest } from "@/lib/queryClient";
import type { WorkRequestR2Attachment } from "@shared/work-request";

type SignedUpload = WorkRequestR2Attachment & {
  uploadUrl: string;
  requiredHeaders?: Record<string, string>;
};

function putFileToR2(
  uploadUrl: string,
  file: File,
  requiredHeaders?: Record<string, string>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    const contentType =
      requiredHeaders?.["Content-Type"] ||
      file.type ||
      "application/octet-stream";
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          "Network/CORS error talking to storage. Ensure R2 bucket CORS allows this origin and Content-Type.",
        ),
      );
    xhr.send(file);
  });
}

export async function uploadIntakeAttachments(
  sessionId: string,
  files: File[],
): Promise<WorkRequestR2Attachment[]> {
  if (files.length === 0) return [];

  const response = await apiRequest(
    "POST",
    "/api/intake/attachments/upload-urls",
    {
      sessionId,
      files: files.map((file) => ({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      })),
    },
  );
  const signed = (await response.json()) as SignedUpload[];
  if (signed.length !== files.length) {
    throw new Error("Could not prepare all attachment uploads. Please try again.");
  }

  await Promise.all(
    files.map((file, index) => {
      const item = signed[index];
      if (!item?.uploadUrl) {
        throw new Error("Missing upload URL");
      }
      return putFileToR2(item.uploadUrl, file, item.requiredHeaders);
    }),
  );

  return signed.map((item) => ({
    id: item.id,
    filename: item.filename,
    r2Key: item.r2Key,
    contentType: item.contentType,
    sizeBytes: item.sizeBytes,
  }));
}
